/**
 * PricingSection 定價 (S9.1 → S5.1 extension) — product-detail pricing view.
 *
 * Mounted by `ProductDetailPage/index.tsx` under 銷售與定價 via the exported
 * `PRICING_SECTION_MOUNT` data, behind `FeatureGate flag="engines.pricing"`
 * INSIDE the tab (flag off ⇒ the gate renders nothing and the tab still shows
 * CommercialSalesRows + salesAnalytics — hide-don't-disable, no dead tab).
 *
 * Renders (plan §S5.1, board FD7):
 * - per Source (CH/TW) the waterfall card — a LABELED STAGE LIST straight from
 *   `buildUpWaterfall(buildCostBreakdown(record))`: one sanctioned label per
 *   stage (到岸成本/Landed Cost); board alias sets surface in TOOLTIPS only
 *   (`WATERFALL_ALIAS_TOOLTIP_KEYS`); the vocab-banned stage-2 alias lives
 *   only in docs/pricing-aliases.md, never in src;
 * - the cost-estimate card (Y-S2, yellow-sweep 2026-07-29) — the FIRST UI
 *   consumer of the S3.1 `selectEstimatedCost` lib: DD!96 成本估算類型 + the
 *   selected stored figure, closing the DD!119 平均成本 / DD!122 上次採購價
 *   render gap (DD!44's display half already rides the waterfall's Purchase
 *   Cost stage). Product-level, so it renders ONCE beside the per-source
 *   cards — mirroring it into each SourceCard would duplicate the signal
 *   (one-signal lens assert). Missing figure = GLYPH + TEXT dash state,
 *   never 0-as-data; the average-cost credit is a NetSuite read-through →
 *   SourceStampTag + stale badge (renderDash-on-empty contract);
 * - the active level matrix (level × markup × unit price × margin %);
 * - the quantity-schedule card — brackets read-only, badged "from schedule"
 *   when `quantityScheduleId` is set (DD!116), explicit empty state otherwise;
 * - the pricing-group card — the product's ONE group (Discribe-20, PurpleTag)
 *   + customer 折扣卡 examples (大潤發 shape), explicit empty state;
 * - the PL × currency matrix — per-user persisted currency tabs (DD!128–131
 *   使用者選擇, localStorage via the hook); a missing currency renders the
 *   `no_currency_price` unresolved state as GLYPH + TEXT (never blank, never
 *   color-only); `fixedPrice` renders EXACTLY one signal (the lock badge —
 *   no cell tint; Discribe-21);
 * - the customer special-price list for this product;
 * - ONE shared `PriceTracePanel` drawer reused for every resolved number —
 *   never a new trace surface.
 *
 * Engine purity (grep-gate, plan doctrine 3): NO price arithmetic in this
 * file — every number (amounts, formulas, margins) arrives pre-computed from
 * `src/lib/pricing` via `useProductPricing`; the tsx only formats.
 */

import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Empty,
  Space,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  theme,
} from 'antd';
import EditQuantityPricingModal from '../modals/EditQuantityPricingModal';
import type { TableProps } from 'antd';
import { EditOutlined, LockOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PriceTracePanel from '@/components/common/PriceTracePanel';
import PurpleTag from '@/components/common/PurpleTag';
import SourceStampTag from '@/components/common/SourceStampTag';
import { renderDash } from '@/components/common/TableContainer';
import { useElevation } from '@/styles/useElevation';
import { controlHeightTokens } from '@/styles/tokens';
import { useCustomerOptions } from '@/hooks/useCustomers';
import {
  buildCurrencyMatrixRows,
  currencyMatrixCurrencies,
  defaultSourceKeyFor,
  pricingAsOf,
  useProductCostEstimate,
  useProductPricing,
  usePricingDataset,
  useMinimumMarginPct,
  canEditCostBasis,
} from '@/hooks/usePricing';
import { buildCurrencyBandContexts, evaluateMarginFloor } from '@/lib/pricing';
import type { CurrencyBandContext, CurrencyBandFacet } from '@/lib/pricing';
import { useTaxonomyTree } from '@/hooks/useTaxonomy';
import type {
  ProductLevelPrice,
  ProductPricingGroupView,
  ProductQuantityPricingView,
  ProductSourcePricing,
} from '@/hooks/usePricing';
import type { Product } from '@/types/api/product';
import type {
  CurrencyCellResolution,
  CustomerGroupPctDiscount,
  PriceResolution,
  PriceSource,
  PriceWaterfallKey,
  QuantityBracket,
  SpecialItemPrice,
} from '@/types/api/pricing';
import { effectivePricingAnchor } from '@/lib/pricing/anchor';
import type { EffectivePricingAnchor } from '@/lib/pricing/anchor';
import type { QuantityMatrixRow, QuantityMatrixView } from '@/lib/pricing';
import type { QuantityPricingScope } from '@/types/api/pricing';
import { formatDiscountBadge, formatMoney, formatPct } from '../../../salesOrders/components/soFormat';
import { DismissibleTooltip } from '../../../salesOrders/components/DismissibleTooltip';
import { visibleWaterfallRows } from '../salesControlsVisibility';
import SectionHeader from './SectionHeader';

const { Text } = Typography;

/**
 * W5-2 / RULING-1(c) — shared collapsed row budget for BOTH level cards
 * (Active Level Prices AND 售價矩陣預覽): first-N by sortOrder, so the
 * collapsed view can never be empty (the old named-vs-PL split rendered an
 * empty table once every active ruled level was a PL row). N = 5 surfaces the
 * whole retail block (PL01–PL05) before the fold.
 */
const COLLAPSED_LEVEL_ROWS = 5;

/* eslint-disable react-refresh/only-export-components -- the mount data and
   the alias-tooltip table must live in the SAME module as the section they
   describe (registry-as-data; asserted by __tests__/pricingSectionMount). */

/**
 * S5.1 mount point — which detail tab hosts this section. Consumed by
 * `ProductDetailPage/index.tsx` (the sole index owner this wave); a future
 * re-homing ruling is a data edit here, mirroring `COMMERCIAL_ROW_MOUNTS`.
 */
export const PRICING_SECTION_MOUNT = 'salesPricing' as const;

/**
 * Board alias sets (docs/pricing-aliases.md §1) — TOOLTIPS ONLY, one
 * sanctioned label per stage stays the rendered text. Keys into the
 * `pricing` namespace; only stages with board aliases are listed.
 */
export const WATERFALL_ALIAS_TOOLTIP_KEYS: Partial<Record<PriceWaterfallKey, string>> = {
  purchase_cost: 'pricing:pricing.waterfallAliases.purchaseCost',
  landed_allocation: 'pricing:pricing.waterfallAliases.landedAllocation',
  landed_cost: 'pricing:pricing.waterfallAliases.landedCost',
  pricing_base: 'pricing:pricing.waterfallAliases.pricingBase',
  list_price: 'pricing:pricing.waterfallAliases.listPrice',
};

/* eslint-enable react-refresh/only-export-components */

interface PricingSectionProps {
  product: Product;
}

/** Trace-drawer request — ONE shared PriceTracePanel serves every consumer. */
interface TraceRequest {
  resolution: PriceResolution;
  currency: string;
}

type OpenTrace = (resolution: PriceResolution, currency: string) => void;

// ---------------------------------------------------------------------------
// Waterfall card body — labeled stage list (ux-lens pin: list by default;
// proportional bars, if ever drawn, must map 1:1 to amounts)
// ---------------------------------------------------------------------------

const WaterfallStageList = ({ source }: { source: ProductSourcePricing }) => {
  const { t } = useTranslation(['pricing']);
  const { token } = theme.useToken();

  return (
    <div role="list" style={{ marginBottom: token.marginSM }}>
      {visibleWaterfallRows(source.waterfallRows).map((stage) => {
        const aliasKey = WATERFALL_ALIAS_TOOLTIP_KEYS[stage.key];
        const label = (
          <Text
            style={
              aliasKey
                ? { textDecoration: 'underline dotted', textUnderlineOffset: 3, cursor: 'help' }
                : undefined
            }
          >
            {t(`pricing:${stage.labelKey}`)}
          </Text>
        );
        return (
          <div
            key={stage.key}
            role="listitem"
            className="tw-flex tw-items-baseline tw-gap-3"
            style={{ padding: `${token.paddingXXS}px 0` }}
          >
            <span style={{ minWidth: 140 }}>
              {aliasKey ? <Tooltip title={t(aliasKey)}>{label}</Tooltip> : label}
            </span>
            <Text
              type="secondary"
              className="tw-flex-1"
              style={{ fontVariantNumeric: 'tabular-nums', fontSize: token.fontSizeSM }}
            >
              {stage.formula ?? ''}
            </Text>
            <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
              {stage.amount.toFixed(2)}
            </Text>
          </div>
        );
      })}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Per-source card — waterfall stage list + level matrix + trace affordance
// ---------------------------------------------------------------------------

const SourcePane = ({
  source,
  anchor,
  quantityScope,
  onOpenTrace,
  vendorName,
}: {
  source: ProductSourcePricing;
  /** D17 EFFECTIVE anchor (product's own pick beats the company default) — read-only chip. */
  anchor: EffectivePricingAnchor | null;
  /** Drives the matrix caption's card-holders note. */
  quantityScope: QuantityPricingScope;
  onOpenTrace: OpenTrace;
  /**
   * R6 (RULING-10) — the vendor linked to this pane's EFFECTIVE record
   * (`record.vendorId` resolved against the product's vendor lines). Null when
   * the record is unlinked (runtime-created) — the stamp degrades honestly to
   * the source-only wording.
   */
  vendorName?: string | null;
}) => {
  const { t } = useTranslation(['pricing', 'common']);
  // Minimum margin (2026-09-15): the live company floor — the read-back chip
  // flags a list price whose margin sits below it (sell price stays still;
  // the operator decides on a price update).
  const minimumMarginPct = useMinimumMarginPct();
  const { token } = theme.useToken();
  // V3 polish (Melissa 2026-08-27, re-based 2026-08-29 RULING-1(c)): the PL
  // ladder collapses by default — first COLLAPSED_LEVEL_ROWS rows visible,
  // the full ladder expands on demand.
  const [showAllLevels, setShowAllLevels] = useState(false);

  const levelColumns: TableProps<ProductLevelPrice>['columns'] = [
    {
      title: t('pricing:pricing.productSection.levelColumns.level'),
      key: 'level',
      width: 200,
      render: (_: unknown, row) => (
        <span style={{ fontWeight: token.fontWeightStrong }}>{row.level.code}</span>
      ),
    },
    {
      title: t('pricing:pricing.productSection.levelColumns.markup'),
      key: 'markup',
      width: 120,
      align: 'right',
      render: (_: unknown, row) =>
        row.markupPct === null ? (
          <Text type="secondary">{t('pricing:pricing.productSection.noLevelRule')}</Text>
        ) : (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {row.markupPct >= 0 ? '+' : ''}
            {row.markupPct}%
          </span>
        ),
    },
    {
      title: t('pricing:pricing.productSection.levelColumns.price'),
      key: 'price',
      width: 140,
      align: 'right',
      render: (_: unknown, row) =>
        row.unitPrice === null ? (
          renderDash()
        ) : (
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              fontWeight: token.fontWeightStrong,
              color: token.colorPrimary,
            }}
          >
            {formatMoney(source.breakdown.currency, row.unitPrice)}
          </span>
        ),
    },
    {
      title: t('pricing:pricing.productSection.levelColumns.margin'),
      key: 'margin',
      width: 120,
      align: 'right',
      render: (_: unknown, row) =>
        row.marginPct === null ? (
          renderDash()
        ) : (
          <span
            style={{
              fontVariantNumeric: 'tabular-nums',
              color: row.marginPct < 0 ? token.colorError : token.colorSuccess,
            }}
          >
            {row.marginPct.toFixed(2)}%
          </span>
        ),
    },
  ];

  const margin = source.listResolution.marginVsLanded;

  // V3 density (WP4 approved direction): levels WITH a rule render as rows;
  // the no-rule tail collapses into ONE footer line pointing at the control
  // surface (Price Setup › Group Matrix). The ruled ladder collapses to the
  // first COLLAPSED_LEVEL_ROWS rows (RULING-1(c) row-count collapse — the
  // named-vs-PL split died with the LEVEL rows; tenant-added named levels get
  // high sortOrder and ride the same first-N rule).
  const ruledLevels = source.levelPrices.filter((row) => row.markupPct !== null);
  const unruledCount = source.levelPrices.length - ruledLevels.length;
  const visibleLevels = showAllLevels
    ? ruledLevels
    : ruledLevels.slice(0, COLLAPSED_LEVEL_ROWS);

  return (
    <div>
      <div className="tw-flex tw-gap-2 tw-flex-wrap tw-items-center" style={{ marginBottom: token.marginMD }}>
        {/* S1.3 source provenance — the pane's ladder derives from THIS
            source's cost record, so the display names it (CH · China / TW ·
            Taiwan) with the record's effective date as the stamp. Defuses the
            CH-vs-TW twin ladders reading as a defect: a screenshot of either
            pane is self-identifying. Same date-only stamp precedent as the
            CostBasisTab FX card. */}
        <SourceStampTag
          sourceLabel={
            vendorName
              ? // R6 (RULING-10) — the pane shows ONE cost basis, so it names
                // the ONE linked vendor (the effective record's, never a list).
                t('pricing:pricing.productSection.sourceProvenanceWithVendor', {
                  source: t(`pricing:pricing.sources.${source.source}`),
                  vendor: vendorName,
                })
              : t('pricing:pricing.productSection.sourceProvenance', {
                  source: t(`pricing:pricing.sources.${source.source}`),
                })
          }
          syncedAt={source.record.effectiveDate}
        />
        <Tag color={source.record.basis === 'actual' ? 'green' : 'orange'} style={{ margin: 0 }}>
          {t(
            `pricing:pricing.fields.${
              source.record.basis === 'estimated' ? 'costBasisEstimated' : 'costBasisActual'
            }`
          )}
        </Tag>
        {/* Price Band, and THE QUANTITY IT WAS BANDED ON.
            RULING R-BAND (2026-08-31) moved the markup multiplication to the
            Pricing Anchor's figure but deliberately left the band lookup on
            the Pricing Base, so the A–D axis stays comparable across products.
            The accepted cost of that ruling is that a `purchase_cost`-anchored
            product is banded on a number its own Edit Commercial modal no
            longer shows — so the chip NAMES it. A hidden input has to be at
            least a disclosed one; this chip is that disclosure, and it is a
            required half of the ruling, not decoration. */}
        <Tag color="purple" style={{ margin: 0 }}>
          {t('pricing:pricing.productSection.bandLabel')}
          {': '}
          {source.band ? source.band.group : t('pricing:pricing.productSection.noBand')}
          {source.band && (
            <>
              {' · '}
              {t('pricing:pricing.productSection.bandBasis', {
                basis: t('pricing:pricing.anchor.pricing_base'),
                amount: formatMoney(source.breakdown.currency, source.breakdown.pricingBase),
              })}
            </>
          )}
        </Tag>
        {/* D17 — anchor read-back chip; the CONTROL lives on Price Setup /
            Edit Commercial Data. Hidden when nothing is set anywhere (absent
            ⇒ no chip, never a fabricated value). 產品自選 marks a product's
            own pick beating the company default (Melissa 2026-08-28). */}
        {anchor && (
          // Same Escape behaviour as the margin tags beside it — two keyboard
          // behaviours on one panel is its own defect (2026-09-16).
          <DismissibleTooltip title={t('pricing:pricing.productSection.anchorTooltip')}>
            <Tag tabIndex={0} style={{ margin: 0, borderStyle: 'dashed' }}>
              {t('pricing:pricing.productSection.anchorChip')}
              {': '}
              {t(`pricing:pricing.anchor.${anchor.anchor}`)}
              {/* Own picks carry NO marker (Melissa 2026-08-28 “產品自選 remove
                  this”) — only the company default names its source. */}
              {anchor.source === 'company' && (
                <>
                  {' · '}
                  {t('pricing:pricing.productSection.anchorFromSetup')}
                </>
              )}
            </Tag>
          </DismissibleTooltip>
        )}
        <Tag style={{ margin: 0 }}>
          {t('pricing:pricing.productSection.listPriceLabel')}
          {': '}
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {/* Option B: the ANCHORED list price, so this chip and the Price
                Trace's closing row (which comes from the resolver) print the
                same number. `breakdown.listPrice` is always base-anchored and
                would fork from the trace under any other anchor. */}
            {formatMoney(source.breakdown.currency, source.listPrice)}
          </span>
        </Tag>
        {margin && (
          <Tag color={margin.pct < 0 ? 'red' : 'green'} style={{ margin: 0 }}>
            {t('pricing:pricing.productSection.marginVsLanded')}
            {': '}
            <span style={{ fontVariantNumeric: 'tabular-nums' }}>
              {formatMoney(source.breakdown.currency, margin.amount)} · {margin.pct.toFixed(2)}%
            </span>
          </Tag>
        )}
        {/* Minimum margin (2026-09-15): the policy tag sits BESIDE the margin
            chip — the loss tier (red) and the policy tier (orange) stay
            distinct, and the referent label is never replaced. */}
        {margin &&
          evaluateMarginFloor({
            unitPrice: source.listPrice,
            landedCost: source.breakdown.landedCost,
            floorPct: minimumMarginPct,
            comparable: true,
          }).status === 'below_floor' && (
            <DismissibleTooltip
              // A rep cannot open Pricing Settings — tell her the fact, not a
              // place she cannot go (2026-09-16).
              title={t(
                canEditCostBasis()
                  ? 'pricing:pricing.productSection.belowMinimumMarginHint'
                  : 'pricing:pricing.productSection.belowMinimumMarginHintViewer',
                {
                  margin: formatPct(margin.pct),
                  floor: formatPct(minimumMarginPct),
                }
              )}
            >
              <Tag color="orange" style={{ margin: 0 }} tabIndex={0}>
                {t('pricing:pricing.productSection.belowMinimumMargin')}
              </Tag>
            </DismissibleTooltip>
          )}
        <Button
          type="link"
          size="small"
          style={{ paddingInline: 0 }}
          onClick={() => onOpenTrace(source.listResolution, source.breakdown.currency)}
        >
          {t('pricing:pricing.productSection.traceButton')}
        </Button>
      </div>

      {/* V3 two-column read-back (WP4): build-up left · level matrix right;
          stacks on narrow viewports. */}
      <div className="tw-grid tw-grid-cols-1 xl:tw-grid-cols-2 tw-gap-6">
        <div>
          {/* Build-up waterfall — labeled stage list from the pure engine */}
          <Text strong style={{ display: 'block', marginBottom: token.marginXXS }}>
            {t('pricing:pricing.productSection.buildUpTitle')}
          </Text>
          <WaterfallStageList source={source} />
        </div>

        <div>
          {/* Active level matrix for this source */}
          <Text strong style={{ display: 'block', marginBottom: token.marginXXS }}>
            {t('pricing:pricing.productSection.levelMatrixTitle')}
          </Text>
          <Table<ProductLevelPrice>
            columns={levelColumns}
            dataSource={visibleLevels}
            rowKey={(row) => row.level.id}
            size="small"
            pagination={false}
            scroll={{ x: 580 }}
            footer={
              ruledLevels.length > COLLAPSED_LEVEL_ROWS || unruledCount > 0
                ? () => (
                    <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                      {ruledLevels.length > COLLAPSED_LEVEL_ROWS && (
                        <Button
                          type="link"
                          size="small"
                          style={{ paddingInline: 0 }}
                          onClick={() => setShowAllLevels((prev) => !prev)}
                        >
                          {showAllLevels
                            ? t('pricing:pricing.productSection.collapseLevels')
                            : t('pricing:pricing.productSection.showAllLevels', {
                                count: ruledLevels.length,
                              })}
                        </Button>
                      )}
                      {unruledCount > 0 && (
                        <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                          {t('pricing:pricing.productSection.noRuleCollapsed', {
                            count: unruledCount,
                          })}
                        </Text>
                      )}
                    </span>
                  )
                : undefined
            }
          />
        </div>
      </div>

      {/* 售價矩陣預覽 — inside the SOURCE pane on purpose: CH and TW carry
          different markup rules, so one shared grid would be a lie. The card
          restates its source (S1.3 provenance) for the same reason. */}
      <div style={{ marginTop: token.marginMD }}>
        <QuantityMatrixCard
          matrix={source.quantityMatrix}
          scope={quantityScope}
          sourceLabel={t('pricing:pricing.productSection.sourceProvenance', {
            source: t(`pricing:pricing.sources.${source.source}`),
          })}
        />
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cost-estimate card (Y-S2) — DD!96 type + the lib-selected stored figure.
// Read-only: the hook credits stored fields, `selectEstimatedCost` picks, the
// tsx ONLY formats (engine-purity grep-gate). `buildCostBreakdown` stays the
// one waterfall — this card derives nothing.
// ---------------------------------------------------------------------------

const CostEstimateCard = ({ product }: { product: Product }) => {
  const { t } = useTranslation(['pricing']);
  const { token } = theme.useToken();
  const elevation = useElevation();
  const estimate = useProductCostEstimate(product);

  return (
    <Card
      title={t('pricing:pricing.productSection.costEstimate.title')}
      style={{ ...elevation.card }}
    >
      {estimate.type === null ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('pricing:pricing.productSection.costEstimate.empty')}
        />
      ) : (
        <>
          <div
            className="tw-flex tw-gap-2 tw-items-center tw-flex-wrap"
            style={{ marginBottom: token.marginSM }}
          >
            <Tag style={{ margin: 0 }}>
              {t('pricing:pricing.productSection.costEstimate.typeLabel')}
              {': '}
              {t(`pricing:pricing.costEstimateType.${estimate.type}`)}
            </Tag>
            {/* DD!119 credit is a NetSuite read-through echo — provenance
                stamp + stale badge ride the shared read-through contract. */}
            {estimate.type === 'average_cost' && estimate.readThroughSyncedAt && (
              <SourceStampTag
                sourceLabel={t('pricing:pricing.productSection.costEstimate.sourceLabel')}
                syncedAt={estimate.readThroughSyncedAt}
              />
            )}
            {estimate.type === 'average_cost' && estimate.readThroughStale && (
              <Tag color="orange" style={{ margin: 0 }}>
                {t('pricing:pricing.productSection.costEstimate.staleBadge')}
              </Tag>
            )}
          </div>
          <div className="tw-flex tw-items-baseline tw-gap-3">
            <span style={{ minWidth: 140 }}>
              <Text>{t('pricing:pricing.productSection.costEstimate.amountLabel')}</Text>
            </span>
            {estimate.selection === null ? (
              // Figure absent / unresolved — GLYPH + TEXT (lens assert),
              // never a blank cell, never a fabricated 0.
              <span className="tw-inline-flex tw-items-center tw-gap-1">
                <MinusCircleOutlined
                  aria-hidden
                  style={{ color: token.colorTextTertiary, fontSize: token.sizeXS }}
                />
                <Text type="secondary">
                  {t('pricing:pricing.productSection.costEstimate.noFigure')}
                </Text>
              </span>
            ) : (
              <Text strong style={{ fontVariantNumeric: 'tabular-nums' }}>
                {estimate.currency
                  ? formatMoney(estimate.currency, estimate.selection.amount)
                  : estimate.selection.amount.toFixed(2)}
              </Text>
            )}
          </div>
          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXS, fontSize: token.fontSizeSM }}
          >
            {t('pricing:pricing.productSection.costEstimate.hint')}
          </Text>
        </>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Quantity-schedule card (DD!116) — read-only brackets, explicit empty state
// ---------------------------------------------------------------------------

const QuantityScheduleCard = ({
  view,
  onEdit,
}: {
  view: ProductQuantityPricingView;
  onEdit: () => void;
}) => {
  const { t, i18n } = useTranslation(['pricing', 'common']);
  const { token } = theme.useToken();
  const elevation = useElevation();
  const zh = i18n.language.startsWith('zh');

  const bracketColumns: TableProps<QuantityBracket>['columns'] = [
    {
      title: t('pricing:pricing.productSection.quantityCard.columns.minQuantity'),
      dataIndex: 'minQuantity',
      key: 'minQuantity',
      width: 180,
      render: (value: number) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      ),
    },
    {
      title: t('pricing:pricing.productSection.quantityCard.columns.discountPct'),
      key: 'discountPct',
      align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {t('pricing:pricing.productSection.quantityCard.discountValue', {
            pct: row.discountPct,
          })}
        </span>
      ),
    },
  ];

  return (
    <Card
      title={t('pricing:pricing.productSection.quantityCard.title')}
      style={{ ...elevation.card }}
      extra={
        // App-wide Edit affordance (Melissa 2026-08-28): primary purple ＋
        // EditOutlined ＋ the 44px height token — same control everywhere.
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={onEdit}
          style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
        >
          {t('common:actions.edit')}
        </Button>
      }
    >
      {/* A dormant schedule is its OWN state — never "no quantity pricing":
          the operator must see WHICH schedule is out of period and why. */}
      {view.outsidePeriod && view.schedule && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: token.marginSM }}
          message={t('pricing:pricing.productSection.quantityCard.outsidePeriodTitle', {
            code: view.schedule.code,
          })}
          description={`${view.schedule.appliesFrom ?? t('pricing:pricing.schedulesAdmin.periodOpenStart')} — ${
            view.schedule.appliesTo ?? t('pricing:pricing.schedulesAdmin.periodOpenEnd')
          }`}
        />
      )}
      {view.scope === 'card_holders' && (
        <Tag color="blue" style={{ marginBottom: token.marginSM }}>
          {t('pricing:pricing.productSection.quantityCard.scopeCardHoldersTag')}
        </Tag>
      )}
      {view.mode === 'none' ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('pricing:pricing.productSection.quantityCard.empty')}
        />
      ) : (
        <>
          <div
            className="tw-flex tw-gap-2 tw-items-center tw-flex-wrap"
            style={{ marginBottom: token.marginSM }}
          >
            {view.mode === 'schedule' && view.schedule ? (
              <>
                <PurpleTag style={{ margin: 0 }}>
                  {t('pricing:pricing.productSection.quantityCard.fromSchedule', {
                    name: zh ? view.schedule.nameZh : view.schedule.nameEn,
                    code: view.schedule.code,
                  })}
                </PurpleTag>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {t('pricing:pricing.productSection.quantityCard.fromScheduleReadOnly')}
                </Text>
              </>
            ) : (
              // RULING-1(b) — ad-hoc brackets share the schedule presentation:
              // same PurpleTag + secondary-hint pair, ownership copy swapped.
              <>
                <PurpleTag style={{ margin: 0 }}>
                  {t('pricing:pricing.productSection.quantityCard.adHoc')}
                </PurpleTag>
                <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                  {t('pricing:pricing.productSection.quantityCard.adHocHint')}
                </Text>
              </>
            )}
          </div>
          <Table<QuantityBracket>
            columns={bracketColumns}
            dataSource={view.brackets}
            rowKey={(row) => String(row.minQuantity)}
            size="small"
            pagination={false}
          />
        </>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// 售價矩陣預覽 — price levels × quantity tiers, FINAL unit price per cell
// ---------------------------------------------------------------------------

/**
 * Read-only preview of the resolved ladder at each quantity tier (Melissa
 * 2026-08-28 "the pricing quantity pricing show with different matrix in the
 * product"). Every number is derived by the SAME pure functions the resolver
 * runs — no stored cells, nothing editable (FABLE C2-applicability §4).
 *
 * Research-bound presentation:
 * - final unit price in every cell, never a %-only cell (NetSuite matrix
 *   orientation ＋ Baymard's show-the-computed-price rule);
 * - the discount badge sits UNDER the price and names its referent — this
 *   row's own ×1 price — because 標準售價 is 3–4 referents in SenditSell and
 *   an unnamed badge would be misinformation;
 * - a mandatory caption states the bracket semantics (all-units) and the
 *   aggregation scope (per line): the identical grid means two different
 *   order totals otherwise;
 * - zero/absent discounts render no badge (shipped hide-when-0 rule).
 */
const QuantityMatrixCard = ({
  matrix,
  scope,
  sourceLabel,
}: {
  matrix: QuantityMatrixView;
  scope: QuantityPricingScope;
  /**
   * S1.3 provenance — the matrix is per SOURCE (CH/TW carry different markup
   * rules), so the card names its source in its own chrome: cropped out of
   * the pane, the twin grids would otherwise read as contradictory data.
   */
  sourceLabel?: string;
}) => {
  const { t, i18n } = useTranslation(['pricing', 'common']);
  const { token } = theme.useToken();
  const elevation = useElevation();
  const zh = i18n.language.startsWith('zh');
  // RULING-1(c) — same first-N collapse as Active Level Prices (shared
  // COLLAPSED_LEVEL_ROWS, per-card local state, no persistence).
  const [showAllLevels, setShowAllLevels] = useState(false);

  if (matrix.rows.length === 0) return null;

  const columns: TableProps<QuantityMatrixRow>['columns'] = [
    {
      title: t('pricing:pricing.productSection.quantityMatrix.columns.level'),
      key: 'level',
      width: 200,
      fixed: 'left',
      render: (_: unknown, row) => (
        <div className="tw-flex tw-items-center tw-gap-2">
          <PurpleTag style={{ margin: 0 }}>{row.level.code}</PurpleTag>
          <Text style={{ fontSize: token.fontSizeSM }}>
            {zh ? row.level.nameZh : row.level.nameEn}
          </Text>
        </div>
      ),
    },
    ...matrix.qtyTiers.map((qty) => ({
      title: t('pricing:pricing.productSection.quantityMatrix.qtyHeader', { qty }),
      key: `qty-${qty}`,
      align: 'right' as const,
      width: 140,
      render: (_: unknown, row: QuantityMatrixRow) => {
        const cell = row.cells.find((entry) => entry.qty === qty);
        if (!cell) return renderDash();
        const discounted = cell.discountPct > 0;
        return (
          <div className="tw-flex tw-flex-col tw-items-end">
            <span
              style={{
                fontVariantNumeric: 'tabular-nums',
                fontWeight: token.fontWeightStrong,
                color: discounted ? token.colorPrimary : undefined,
              }}
            >
              {formatMoney(matrix.currency, cell.unitPrice)}
            </span>
            {/* hide-when-0: an undiscounted tier carries no badge at all.
                The stored % stays derived from the two ROUNDED prices (so a
                cell can never advertise a discount it did not deliver), but
                the BADGE is shown at ≤1dp — 5.01 reads as 5%, which is the
                policy the operator recognises. The exact money is the cell
                above it. */}
            {discounted && (
              <Text style={{ fontSize: token.fontSizeSM, color: token.colorWarning }}>
                {t('pricing:pricing.productSection.quantityMatrix.offBadge', {
                  pct: formatDiscountBadge(cell.discountPct),
                })}
              </Text>
            )}
          </div>
        );
      },
    })),
  ];

  return (
    <Card
      title={t('pricing:pricing.productSection.quantityMatrix.title')}
      style={{ ...elevation.card }}
      extra={
        sourceLabel ? <Tag style={{ margin: 0 }}>{sourceLabel}</Tag> : undefined
      }
    >
      <Table<QuantityMatrixRow>
        columns={columns}
        dataSource={showAllLevels ? matrix.rows : matrix.rows.slice(0, COLLAPSED_LEVEL_ROWS)}
        rowKey={(row) => row.level.id}
        size="small"
        pagination={false}
        // 'max-content' rather than a computed pixel width: it sizes to the
        // real columns AND keeps arithmetic out of the tsx (the S5.1
        // engine-purity grep-gate is right to flag any * + - / here).
        scroll={{ x: 'max-content' }}
        footer={
          matrix.rows.length > COLLAPSED_LEVEL_ROWS
            ? () => (
                <span className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
                  <Button
                    type="link"
                    size="small"
                    style={{ paddingInline: 0 }}
                    onClick={() => setShowAllLevels((prev) => !prev)}
                  >
                    {showAllLevels
                      ? t('pricing:pricing.productSection.collapseLevels')
                      : t('pricing:pricing.productSection.showAllLevels', {
                          count: matrix.rows.length,
                        })}
                  </Button>
                </span>
              )
            : undefined
        }
      />
      <Text
        type="secondary"
        style={{ display: 'block', marginTop: token.marginXS, fontSize: token.fontSizeSM }}
      >
        {t('pricing:pricing.productSection.quantityMatrix.semanticsCaption')}
        {scope === 'card_holders' && (
          <>
            {' · '}
            {t('pricing:pricing.productSection.quantityMatrix.cardHoldersCaption')}
          </>
        )}
      </Text>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Pricing-group card (Discribe-20) — one group, customer 折扣卡 examples
// ---------------------------------------------------------------------------

const PricingGroupCard = ({
  view,
  customerName,
}: {
  view: ProductPricingGroupView;
  /** Name-not-id rule (Melissa 2026-08-27) — resolver shared with the section. */
  customerName: (id: number) => string;
}) => {
  const { t, i18n } = useTranslation(['pricing', 'common']);
  const { token } = theme.useToken();
  const elevation = useElevation();
  const zh = i18n.language.startsWith('zh');

  const exampleColumns: TableProps<CustomerGroupPctDiscount>['columns'] = [
    {
      title: t('pricing:pricing.productSection.groupCard.columns.customer'),
      dataIndex: 'customerId',
      key: 'customerId',
      width: 200,
      render: (_: number, row) => row.customerName ?? customerName(row.customerId),
    },
    {
      title: t('pricing:pricing.productSection.groupCard.columns.discount'),
      key: 'discount',
      align: 'right',
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {t('pricing:pricing.productSection.groupCard.exampleDiscount', {
            pct: row.discountPct,
          })}
        </span>
      ),
    },
  ];

  return (
    <Card
      title={t('pricing:pricing.productSection.groupCard.title')}
      style={{ ...elevation.card }}
    >
      {view.group === null ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('pricing:pricing.productSection.groupCard.empty')}
        />
      ) : (
        <>
          <div
            className="tw-flex tw-gap-2 tw-items-center tw-flex-wrap"
            style={{ marginBottom: token.marginSM }}
          >
            <PurpleTag style={{ margin: 0 }}>
              {zh ? view.group.nameZh : view.group.nameEn} ({view.group.code})
            </PurpleTag>
            {/* 群組標準折扣 — the group's policy rate. A customer still needs
                a 折扣卡 to actually receive it, so the label says "standard". */}
            {view.group.defaultDiscountPct != null && (
              <Tag style={{ margin: 0 }}>
                {t('pricing:pricing.productSection.groupCard.standardDiscount', {
                  pct: view.group.defaultDiscountPct,
                })}
              </Tag>
            )}
            <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
              {t('pricing:pricing.productSection.groupCard.oneGroupRule')}
            </Text>
          </div>
          <Text strong style={{ display: 'block', marginBottom: token.marginXXS }}>
            {t('pricing:pricing.productSection.groupCard.examplesTitle')}
          </Text>
          {view.discountExamples.length === 0 ? (
            <Text type="secondary">
              {t('pricing:pricing.productSection.groupCard.noExamples')}
            </Text>
          ) : (
            <Table<CustomerGroupPctDiscount>
              columns={exampleColumns}
              dataSource={view.discountExamples}
              rowKey="id"
              size="small"
              pagination={false}
            />
          )}
        </>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// PL × currency matrix — per-user persisted tabs; unresolved = glyph + text;
// fixedPrice = the lock badge, EXACTLY one signal (no cell tint)
// ---------------------------------------------------------------------------

const CurrencyCellValue = ({
  cell,
  compact = false,
}: {
  cell: CurrencyCellResolution;
  /** Grid mode: glyph + tooltip per cell (the text legend rides the grid footer once). */
  compact?: boolean;
}) => {
  const { t } = useTranslation(['pricing']);
  const { token } = theme.useToken();

  if (cell.status === 'unresolved') {
    // `no_currency_price` — GLYPH + TEXT, never a blank cell, never
    // color-only (ux-lens UI-state pin a). Compact grid cells carry the
    // glyph + tooltip/aria text; the visible text legend sits once under
    // the grid.
    if (compact) {
      return (
        <Tooltip
          trigger={['hover', 'focus']}
          title={t('pricing:pricing.productSection.currencyMatrix.noCurrencyPrice')}
        >
          <MinusCircleOutlined
            tabIndex={0}
            aria-label={t('pricing:pricing.productSection.currencyMatrix.noCurrencyPrice')}
            style={{ color: token.colorTextTertiary, fontSize: token.sizeXS }}
          />
        </Tooltip>
      );
    }
    return (
      <span className="tw-inline-flex tw-items-center tw-gap-1">
        <MinusCircleOutlined
          aria-hidden
          style={{ color: token.colorTextTertiary, fontSize: token.sizeXS }}
        />
        <Text type="secondary">
          {t('pricing:pricing.productSection.currencyMatrix.noCurrencyPrice')}
        </Text>
      </span>
    );
  }

  return (
    <span className="tw-inline-flex tw-items-center tw-gap-2">
      <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: token.fontWeightStrong }}>
        {formatMoney(cell.row.currency, cell.row.price)}
      </span>
      {cell.row.fixedPrice && (
        // Discribe-21 — EXACTLY one signal: this badge. No cell tint.
        <Tooltip title={t('pricing:pricing.productSection.currencyMatrix.fixedPriceHint')}>
          <Tag icon={<LockOutlined />} style={{ margin: 0 }}>
            {t('pricing:pricing.productSection.currencyMatrix.fixedPriceBadge')}
          </Tag>
        </Tooltip>
      )}
    </span>
  );
};

/**
 * P3 (2026-08-31) — ONE currency column's BAND CONTEXT, rendered under the
 * currency code in the grid header.
 *
 * WHY IT IS HERE AT ALL. The grid's cells are manually-locked prices, which
 * say nothing about which band set a product is bucketed on — so after P1 gave
 * product groups their own band sets, two products in DIFFERENT groups still
 * rendered identical matrix chrome. This line is where the per-group scoping
 * becomes visible: the band the product actually resolves into IN THIS
 * CURRENCY, and that band's bounds.
 *
 * WHAT IT MUST NEVER DO. No FX. A currency the product has no cost basis in
 * gets the dash state (glyph + text), exactly like an unlocked price cell —
 * never a converted figure, never a bound borrowed from another currency. The
 * engine's own `resolveCurrencyBandContext` carries that fence in its
 * signature (there is no rate to pass it); this component only formats what it
 * returns.
 */
const CurrencyBandHeaderLine = ({ context }: { context: CurrencyBandContext }) => {
  const { t } = useTranslation(['pricing']);
  const { token } = theme.useToken();

  const rangeLabel = (facet: CurrencyBandFacet): string => {
    const band = facet.band;
    if (!band) return '';
    return band.maxExclusive === null
      ? t('pricing:pricing.productSection.currencyMatrix.bandRangeOpen', {
          min: formatMoney(context.currency, band.minInclusive),
        })
      : t('pricing:pricing.productSection.currencyMatrix.bandRange', {
          min: formatMoney(context.currency, band.minInclusive),
          max: formatMoney(context.currency, band.maxExclusive),
        });
  };

  if (context.facets.length === 0) {
    // GLYPH + TEXT, the same unresolved idiom the price cells use — a missing
    // cost basis is a missing band, not a zero and not a converted number.
    return (
      <span
        className="tw-inline-flex tw-items-center tw-gap-1"
        style={{ fontSize: token.fontSizeSM, color: token.colorTextTertiary, fontWeight: 400 }}
      >
        <MinusCircleOutlined aria-hidden style={{ fontSize: token.sizeXS }} />
        {t('pricing:pricing.productSection.currencyMatrix.noCostBasisInCurrency')}
      </span>
    );
  }

  return (
    <span
      style={{
        display: 'grid',
        gap: '2px',
        fontSize: token.fontSizeSM,
        color: token.colorTextSecondary,
        fontWeight: 400,
      }}
    >
      {context.facets.map((facet) => {
        // Only name the source when the product's two cost records land on
        // DIFFERENT outcomes — otherwise the prefix is noise on every row.
        const prefix =
          context.facets.length > 1
            ? `${facet.sources.map((source) => t(`pricing:pricing.sources.${source}`)).join(' / ')} · `
            : '';
        return (
          <span
            key={`${facet.band?.id ?? 'none'}-${facet.pricingBase}`}
            style={{ fontVariantNumeric: 'tabular-nums' }}
          >
            {facet.band
              ? `${prefix}${t('pricing:pricing.productSection.currencyMatrix.bandChip', {
                  group: facet.band.group,
                  range: rangeLabel(facet),
                })}`
              : `${prefix}${t('pricing:pricing.productSection.currencyMatrix.noBandInCurrency', {
                  amount: formatMoney(context.currency, facet.pricingBase),
                })}`}
          </span>
        );
      })}
    </span>
  );
};

/**
 * V3 consolidated currency grid (Melissa 2026-08-27, "big-tech style"):
 * ONE dense table — rows = List Price + levels, columns = the currencies —
 * replacing the per-currency tabs whose columns were mostly empty. Rows with
 * no locked price in ANY currency collapse into a single count line; an
 * empty cell renders the shared glyph (legend text carried once under the
 * grid, never color-only, never blank). All cells resolve through the same
 * pure `resolveCurrencyCell` the engine uses.
 */
const CurrencyMatrixGrid = ({ productId }: { productId: string }) => {
  const { t, i18n } = useTranslation(['pricing', 'common']);
  const { token } = theme.useToken();
  const elevation = useElevation();
  const { dataset } = usePricingDataset();
  // Read-only, cached taxonomy read — the band SET is owned by a depth-2
  // product-group node, and only the taxonomy carries its name.
  const { nodes: categoryNodes } = useTaxonomyTree();
  const zhLocale = i18n.language.startsWith('zh');

  const { currencies, bandContexts, gridRows, collapsedCount } = useMemo(() => {
    if (!dataset) {
      return { currencies: [], bandContexts: [], gridRows: [], collapsedCount: 0 };
    }
    const currencyList = currencyMatrixCurrencies(dataset);
    const perCurrency = currencyList.map((currency) =>
      buildCurrencyMatrixRows(dataset, productId, currency)
    );
    const slotCount = perCurrency[0]?.length ?? 0;
    const rows = Array.from({ length: slotCount }, (_, index) => ({
      key: perCurrency[0][index].key,
      level: perCurrency[0][index].level,
      cells: perCurrency.map((column) => column[index].cell),
    }));
    const withPrice = rows.filter((row) =>
      row.cells.some((cell) => cell.status === 'resolved')
    );
    return {
      currencies: currencyList,
      // P3 — per-column band context from the PRODUCT'S OWN band set. Pure
      // engine call; this file still computes no price arithmetic of its own.
      bandContexts: buildCurrencyBandContexts(dataset, productId, currencyList, pricingAsOf()),
      gridRows: withPrice,
      collapsedCount: rows.length - withPrice.length,
    };
  }, [dataset, productId]);

  /**
   * P3 — WHICH band set this product is bucketed on, named. The bounds in the
   * column headers are only legible once the reader knows whose set they came
   * from: a product group that owns its own cuts, or the default A–D set every
   * unscoped product shares.
   *
   * ⚠ `bandSetOwnerId`, NOT `productGroupId`. A product's group and the set it
   * is banded on are different questions — a group that owns no cuts is banded
   * on the DEFAULT set, and captioning it with the group's name is a confident
   * wrong answer (this surface shipped that mistake for one run: the brake pad
   * read "Band set: Brake Pads" over the default A–D bounds). The engine's own
   * `resolvedBandSetOwner` decides it, so caption and selection cannot drift.
   * It is a property of the product, not of a currency, so it is read once.
   */
  const bandSetOwnerId = bandContexts[0]?.bandSetOwnerId ?? null;
  const bandSetLabel = useMemo(() => {
    if (!bandSetOwnerId) return t('pricing:pricing.productSection.currencyMatrix.bandSetDefault');
    const node = categoryNodes.find((candidate) => candidate.id === bandSetOwnerId);
    if (!node) return bandSetOwnerId;
    return zhLocale ? node.nameZh : node.nameEn;
  }, [bandSetOwnerId, categoryNodes, t, zhLocale]);

  const gridColumns: TableProps<(typeof gridRows)[number]>['columns'] = [
    {
      title: t('pricing:pricing.productSection.levelColumns.level'),
      key: 'level',
      width: 160,
      render: (_: unknown, row) =>
        row.level === null ? (
          <span style={{ fontWeight: token.fontWeightStrong }}>
            {t('pricing:pricing.productSection.listPriceLabel')}
          </span>
        ) : (
          <span>{row.level.code}</span>
        ),
    },
    ...currencies.map((currency, columnIndex) => ({
      // P3 — the currency code, and UNDER it the band this product resolves
      // into in that currency, with its bounds. Two products in different
      // product groups now read differently here, which is the whole point.
      title: (
        <span style={{ display: 'grid', gap: '2px' }}>
          <span style={{ fontWeight: token.fontWeightStrong }}>{currency}</span>
          {bandContexts[columnIndex] && (
            <CurrencyBandHeaderLine context={bandContexts[columnIndex]} />
          )}
        </span>
      ),
      key: currency,
      align: 'right' as const,
      render: (_: unknown, row: (typeof gridRows)[number]) => (
        <CurrencyCellValue cell={row.cells[columnIndex]} compact />
      ),
    })),
  ];

  return (
    <Card
      title={t('pricing:pricing.productSection.currencyMatrix.title')}
      style={{ ...elevation.card }}
    >
      {currencies.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={t('pricing:pricing.productSection.currencyMatrix.empty')}
        />
      ) : (
        <>
          {/* P3 — WHOSE band set the column bounds above came from. Named
              before the grid, because "Band B · 25.00 – 60.00" is only
              readable once you know it is the Tail Lights set and not the
              default A–D one every unscoped product shares. */}
          <div style={{ marginBottom: token.marginXS }}>
            <Tag color="purple" style={{ margin: 0 }}>
              {t('pricing:pricing.productSection.currencyMatrix.bandSetLabel')}
              {': '}
              {bandSetLabel}
            </Tag>
          </div>
          <Table<(typeof gridRows)[number]>
            columns={gridColumns}
            dataSource={gridRows}
            rowKey="key"
            size="small"
            pagination={false}
            locale={{
              emptyText: (
                <Text type="secondary">
                  {t('pricing:pricing.productSection.currencyMatrix.noneLockedForProduct')}
                </Text>
              ),
            }}
            footer={
              collapsedCount > 0 && gridRows.length > 0
                ? () => (
                    <Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
                      {t('pricing:pricing.productSection.currencyMatrix.collapsedRows', {
                        count: collapsedCount,
                      })}
                    </Text>
                  )
                : undefined
            }
          />
          <Text
            type="secondary"
            style={{ display: 'block', fontSize: token.fontSizeSM, marginTop: token.marginXS }}
          >
            {t('pricing:pricing.productSection.currencyMatrix.gridLegend')}
            {' · '}
            {t('pricing:pricing.productSection.currencyMatrix.lockedHint')}
          </Text>
        </>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// The section
// ---------------------------------------------------------------------------

export const PricingSection = ({ product }: PricingSectionProps) => {
  const { t } = useTranslation(['pricing', 'common']);
  const { token } = theme.useToken();
  const navigate = useNavigate();
  const elevation = useElevation();
  const { sources, overrides, quantityPricing, pricingGroup, isLoading } = useProductPricing(
    product.id
  );
  const { dataset } = usePricingDataset();
  const anchor = effectivePricingAnchor(product, dataset);

  // Customer display rule (Melissa 2026-08-27): always a resolved NAME, never
  // a bare id — same map the Overrides tab builds (`#id` only as the
  // still-loading/unknown fallback).
  const { customers } = useCustomerOptions();
  const customerName = useMemo(() => {
    const byId = new Map(customers.map((c) => [c.customerId, c.customerName]));
    return (id: number) => byId.get(id) ?? `#${id}`;
  }, [customers]);

  const [quantityEditOpen, setQuantityEditOpen] = useState(false);

  // R6 (RULING-10) default-supplier display — controlled, not defaultActiveKey:
  // the card can mount while `sources` is still empty, and defaultActiveKey
  // would freeze before data arrives. The user's explicit pick always wins.
  const [pickedSource, setPickedSource] = useState<PriceSource | null>(null);

  // ONE shared PriceTracePanel serves every resolved number on this section.
  const [traceRequest, setTraceRequest] = useState<TraceRequest | null>(null);
  const openTrace: OpenTrace = (resolution, currency) =>
    setTraceRequest({ resolution, currency });

  const overrideColumns: TableProps<SpecialItemPrice>['columns'] = [
    {
      title: t('pricing:pricing.overrides.fields.customer'),
      dataIndex: 'customerId',
      key: 'customerId',
      // Melissa 2026-08-28: "wider column we have space in the customer" —
      // full company names fit on one line; the reason column absorbs slack.
      width: 320,
      // Name-not-id rule: the denormalized row name wins (survives the mixed
      // seam, where live customer ids need not match mock pricing ids).
      render: (_: number, row) => row.customerName ?? customerName(row.customerId),
    },
    {
      title: t('pricing:pricing.overrides.fields.kind'),
      dataIndex: 'kind',
      key: 'kind',
      width: 110,
      render: (kind: SpecialItemPrice['kind']) => (
        <Tag color={kind === 'fixed' ? 'blue' : 'purple'} style={{ margin: 0 }}>
          {t(`pricing:pricing.overrideKind.${kind === 'fixed' ? 'fixed' : 'markupPct'}`)}
        </Tag>
      ),
    },
    {
      title: t('pricing:pricing.overrides.fields.value'),
      key: 'value',
      width: 170,
      align: 'right',
      render: (_: unknown, row) =>
        row.kind === 'fixed' ? (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {formatMoney(row.currency, row.fixedPrice)}
          </span>
        ) : (
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            {/* The row's OWN recorded basis (`basisAtAuthoring`) — the figure
                the resolver multiplies for this row, which is not necessarily
                the product's anchor today. */}
            {t('pricing:pricing.overrides.valueOfBase', {
              pct: row.markupPct,
              basis: t(`pricing:pricing.anchor.${row.basisAtAuthoring}`),
            })}
          </span>
        ),
    },
    {
      title: t('pricing:pricing.overrides.fields.validity'),
      key: 'validity',
      // 230 keeps the full "YYYY-MM-DD — YYYY-MM-DD" range on one line.
      width: 230,
      render: (_: unknown, row) => (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.validFrom} — {row.validTo ?? t('pricing:pricing.overrides.openEnded')}
        </span>
      ),
    },
    {
      title: t('pricing:pricing.overrides.fields.status'),
      dataIndex: 'isActive',
      key: 'isActive',
      width: 100,
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'default'} style={{ margin: 0 }}>
          {t(`pricing:pricing.overrides.status.${isActive ? 'active' : 'inactive'}`)}
        </Tag>
      ),
    },
    {
      title: t('pricing:pricing.overrides.fields.reason'),
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
    },
  ];

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <div>
        <SectionHeader title={t('pricing:pricing.productSection.title')} />
        {/* Control-vs-display ruling: rules live in Price Setup — say so once. */}
        <Text
          type="secondary"
          style={{ display: 'block', fontSize: token.fontSizeSM, marginBottom: token.marginSM }}
        >
          {t('pricing:pricing.productSection.controlNote')}
        </Text>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {sources.length === 0 && !isLoading ? (
            <Empty description={t('pricing:pricing.productSection.noCostRecords')} />
          ) : (
            /* V3 polish: ONE card, sources as a tab switch (CH/TW) —
               replaces the stacked per-source cards. */
            <Card style={{ ...elevation.card }}>
              <Tabs
                size="small"
                // R6 (RULING-10) — the pane that OPENS follows the PRIMARY
                // vendor line's linked record; both panes stay reachable.
                activeKey={pickedSource ?? defaultSourceKeyFor(product, sources) ?? undefined}
                onChange={(key) => setPickedSource(key as PriceSource)}
                items={sources.map((source) => ({
                  key: source.source,
                  label: t(`pricing:pricing.sources.${source.source}`),
                  children: (
                    <SourcePane
                      source={source}
                      anchor={anchor}
                      quantityScope={quantityPricing.scope}
                      onOpenTrace={openTrace}
                      vendorName={
                        product.vendorLines?.find(
                          (line) => line.vendorId === source.record.vendorId
                        )?.vendorName ?? null
                      }
                    />
                  ),
                }))}
              />
            </Card>
          )}
          {/* Y-S2 — product-level cost-estimate figures (DD!96/119/122; the
              DD!44 display half rides the waterfall's Purchase Cost stage).
              Renders regardless of cost records — the estimate credits
              product-master / vendor-line / read-through figures. */}
          <CostEstimateCard product={product} />
        </Space>
      </div>

      {/* S5.1 — schedule / group / currency-matrix cards (board FD7 order) */}
      {/* V3 polish: quantity + group side-by-side on wide screens. */}
      <div className="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-4">
        <QuantityScheduleCard
          view={quantityPricing}
          onEdit={() => setQuantityEditOpen(true)}
        />
        <PricingGroupCard view={pricingGroup} customerName={customerName} />
      </div>
      <EditQuantityPricingModal
        open={quantityEditOpen}
        productId={product.id}
        onCancel={() => setQuantityEditOpen(false)}
        onSaved={() => setQuantityEditOpen(false)}
      />
      <CurrencyMatrixGrid productId={product.id} />

      <div>
        <div
          className="tw-flex tw-items-center tw-justify-between"
          style={{ marginBottom: token.marginSM }}
        >
          <Typography.Title level={5} style={{ margin: 0 }}>
            {t('pricing:pricing.productSection.overridesTitle')}
          </Typography.Title>
          {/* S3.2 — the ONE product-level entry into the central form: a LINK
              (nothing more) landing on the Overrides tab prefiltered to this
              product via the query param OverridesTab reads. */}
          <Button
            type="link"
            size="small"
            style={{ padding: 0 }}
            onClick={() => navigate(`/auth/pricing/setup/overrides?product=${product.id}`)}
          >
            {t('pricing:pricing.productSection.manageSpecialPrices')}
          </Button>
        </div>
        <Table<SpecialItemPrice>
          columns={overrideColumns}
          dataSource={overrides}
          rowKey="id"
          size="small"
          loading={isLoading}
          rowClassName={(record) => (!record.isActive ? 'inactive-row' : '')}
          pagination={false}
          scroll={{ x: 880 }}
        />
      </div>

      {/* THE shared trace surface — reused for every resolved number */}
      {traceRequest && (
        <PriceTracePanel
          open
          onClose={() => setTraceRequest(null)}
          resolution={traceRequest.resolution}
          unitPrice={null}
          currency={traceRequest.currency}
        />
      )}
    </Space>
  );
};

export default PricingSection;
